import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GroupIcon from '@mui/icons-material/Group';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { OrganizationDto } from '../../api/types/dtos';
import { useOrganizationDetails } from '../../api/hooks/useOrganizationDetails';
import { useCities, useVeredasByCity, useCorregimientos, useRegions, useCity, useCountries } from '../../api/hooks/location.hooks';
import { useCreateFarmLocation, useUpdateFarmLocation } from '../../api/hooks/organization.hooks';
import { useUsers } from '../../api/hooks';
import { useGroups } from '../../api/hooks/useGroups';
import { useAddOrganizationsToGroup, useDeleteGroupRelation } from '../../api/hooks/useGroupsOrganization';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client/axiosClient';
import { ENDPOINTS } from '../../api/endpoints';
import { useSnackbar } from 'notistack';
import CreateEntityModal from './CreateEntityModal';
import styles from './EditFarmDetailsModal.module.scss';

interface EditFarmDetailsModalProps {
  open: boolean;
  onClose: () => void;
  organization: OrganizationDto | null;
}

const EditFarmDetailsModal: React.FC<EditFarmDetailsModalProps> = ({
  open,
  onClose,
  organization
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRegionId, setSelectedRegionId] = useState<string>('');
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUpdatingLogo, setIsUpdatingLogo] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [userDescription, setUserDescription] = useState<string>('');
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [originalUserData, setOriginalUserData] = useState<{
    name: string;
    description: string;
    isOwner: boolean;
  } | null>(null);

  // Estados para la nueva sección de edición de finca y usuario
  const [farmName, setFarmName] = useState<string>('');
  const [farmIdNumber, setFarmIdNumber] = useState<string>('');
  const [userFirstName, setUserFirstName] = useState<string>('');
  const [userLastName, setUserLastName] = useState<string>('');

  // Estados para la gestión de grupos
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [isJoiningGroup, setIsJoiningGroup] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Function to refresh groups data
  const refreshGroupsData = () => {
    // Invalidate organization groups query
    queryClient.invalidateQueries({ queryKey: ['organization-groups', organization?.id] });
    // Invalidate available groups query
    queryClient.invalidateQueries({ queryKey: ['groups', orgDetails?.organizationsUsers?.[0]?.organization?.parentOrganizationId] });
  };

  // Create entity modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalType, setCreateModalType] = useState<'region' | 'city' | 'vereda' | 'corregimiento'>('region');
  const [createModalLoading, setCreateModalLoading] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const [selectedRegionForCity, setSelectedRegionForCity] = useState<string>('');
  const [selectedCityForVereda, setSelectedCityForVereda] = useState<string>('');
  const [selectedCorregimientoForVereda, setSelectedCorregimientoForVereda] = useState<string>('');
  const [selectedCityForCorregimiento, setSelectedCityForCorregimiento] = useState<string>('');

  // Mutation hooks - use empty string as fallback
  const createFarmLocationMutation = useCreateFarmLocation(organization?.id || '');
  const updateFarmLocationMutation = useUpdateFarmLocation(organization?.id || '');

  // Fetch organization details
  const { data: orgDetailsResponse, isLoading: isLoadingDetails, refetch: refetchDetails } = useOrganizationDetails(organization?.id || '');
  const orgDetails = orgDetailsResponse?.data;

  // Fetch groups for the current organization
  const { data: availableGroups, isLoading: isLoadingGroups } = useGroups(orgDetails?.organizationsUsers?.[0]?.organization?.parentOrganizationId || '', {
    isActive: true,
    sortBy: 'name',
    sortDirection: 'ASC'
  });

  // Hook for adding organization to group
  const addToGroupMutation = useAddOrganizationsToGroup();

  // Hook for deleting group relation
  const deleteGroupMutation = useDeleteGroupRelation();

  // Fetch current groups of the organization
  const { data: currentGroupsResponse, isLoading: isLoadingCurrentGroups } = useQuery({
    queryKey: ['organization-groups', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;
      const response = await api.get(ENDPOINTS.organizations.groups(organization.id));
      return (response as any).data;
    },
    enabled: !!organization?.id
  });

  const currentGroups = currentGroupsResponse || [];

  // Filter available groups to exclude those already joined
  const filteredAvailableGroups = Array.isArray(availableGroups) ? (availableGroups as any[]).filter((availableGroup: any) => {
    return !currentGroups.some((currentGroup: any) =>
      currentGroup.group?.id === availableGroup.id
    );
  }) : [];

  // Fetch users for selection - DISABLED as per requirement to not allow changing user
  const { data: usersResponse } = useUsers(undefined, { enabled: false } as any);
  const availableUsers = usersResponse?.data || [];

  // Location form state
  const [locationData, setLocationData] = useState({
    regionId: '',
    cityId: '',
    corregimientoId: '',
    veredaId: '',
    latitude: '',
    longitude: '',
    elevation: '',
    area: ''
  });

  // Socioeconomic form state
  const [socioeconomicData, setSocioeconomicData] = useState({
    malesFullTimeWorkers: '',
    malesTemporalWorkers: '',
    femaleFullTimeWorkers: '',
    femaleTemporalWorkers: ''
  });

  // Location hooks
  const { data: regions } = useRegions();
  const { data: cities } = useCities(selectedRegionId || undefined);
  const { data: corregimientos } = useCorregimientos(undefined);
  const { data: veredas } = useVeredasByCity(locationData.cityId);
  const { data: countries } = useCountries();
  // Get all cities using the findAll endpoint
  const { data: allCities } = useQuery({
    queryKey: ['cities', 'all'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.cities.base());
      return (response as any).data;
    },
  });

  // Get city details to find region
  const { data: cityDetails } = useCity(locationData.cityId || undefined);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Logo handling functions
  const handleLogoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Por favor selecciona un archivo de imagen válido', { variant: 'error' });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar('El archivo es demasiado grande. Máximo 5MB', { variant: 'error' });
        return;
      }

      setSelectedLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoUpdate = async () => {
    if (!selectedLogoFile || !organization?.id) return;

    setIsUpdatingLogo(true);
    try {
      const formData = new FormData();
      formData.append('organization', JSON.stringify({}));
      formData.append('logo', selectedLogoFile);

      await api.put(`${ENDPOINTS.organizations.base()}/${organization.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Reset logo state
      setSelectedLogoFile(null);
      setLogoPreview(null);

      // Refresh organization details
      if (refetchDetails) {
        refetchDetails();
      }
    } catch (error) {
      console.error('Error updating logo:', error);
      throw error;
    } finally {
      setIsUpdatingLogo(false);
    }
  };

  const handleUserSelectionChange = (newUserId: string) => {
    setSelectedUserId(newUserId);

    const currentOrgUser = orgDetails?.organizationsUsers?.[0];
    const currentUserId = currentOrgUser?.user?.id;

    if (newUserId === currentUserId) {
      if (originalUserData) {
        setUserName(originalUserData.name);
        setUserDescription(originalUserData.description);
        setIsOwner(originalUserData.isOwner);
      }
    } else {
      setUserName('');
      setUserDescription('');
      setIsOwner(false);
    }
  };

  const handleFarmDataUpdate = async () => {
    if (!organization?.id || (!farmName.trim() && !farmIdNumber.trim())) return;

    try {
      const formData = new FormData();

      // Create organization object with the fields
      const organizationData: any = {};

      if (farmName.trim()) {
        organizationData.name = farmName.trim();
      }

      if (farmIdNumber.trim()) {
        organizationData.idNumber = farmIdNumber.trim();
      }

      // Append the organization object as JSON string
      formData.append('organization', JSON.stringify(organizationData));

      await api.put(ENDPOINTS.organizations.base() + `/${organization.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (refetchDetails) {
        refetchDetails();
      }
    } catch (error) {
      console.error('Error updating farm data:', error);
      throw error;
    }
  };

  const handleUserDataUpdate = async () => {
    if (!orgDetails?.organizationsUsers?.[0]?.user?.id) return;

    try {
      const updateData = {
        name: userFirstName.trim(),
        lastName: userLastName.trim()
      };

      await api.put(ENDPOINTS.users.update(orgDetails.organizationsUsers[0].user.id), updateData);

      if (refetchDetails) {
        refetchDetails();
      }
    } catch (error) {
      console.error('Error updating user data:', error);
      throw error;
    }
  };

  const handleJoinGroup = async (groupId?: string) => {
    const groupToJoin = groupId || selectedGroup;
    const parentOrgId = orgDetails?.organizationsUsers?.[0]?.organization?.parentOrganizationId;

    if (!groupToJoin || !parentOrgId || !organization?.id) {
      enqueueSnackbar('Error al procesar la solicitud', { variant: 'warning' });
      return;
    }

    setIsJoiningGroup(true);
    try {
      await addToGroupMutation.mutateAsync({
        groupId: groupToJoin,
        organizationIds: [organization.id] // Usar el ID de la organización actual (finca)
      });

      enqueueSnackbar('Te has unido al grupo exitosamente', { variant: 'success' });
      setSelectedGroup('');

      // Refresh groups data to show changes immediately
      refreshGroupsData();

      // Refresh organization details to get updated group information
      if (refetchDetails) {
        refetchDetails();
      }
    } catch (error) {
      console.error('Error joining group:', error);
      enqueueSnackbar('Error al unirse al grupo', { variant: 'error' });
    } finally {
      setIsJoiningGroup(false);
    }
  };

  const handleLeaveGroup = async (groupOrg: any) => {
    if (!organization?.id || !groupOrg.group?.id || !groupOrg.id) {
      enqueueSnackbar('Error al procesar la solicitud', { variant: 'warning' });
      return;
    }

    try {
      await deleteGroupMutation.mutateAsync({
        organizationId: organization.id,
        groupId: groupOrg.group.id,
        groupOrganizationId: groupOrg.id
      });

      enqueueSnackbar('Has salido del grupo exitosamente', { variant: 'success' });

      // Refresh groups data to show changes immediately
      refreshGroupsData();

      // Refresh organization details to get updated group information
      if (refetchDetails) {
        refetchDetails();
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      enqueueSnackbar('Error al salir del grupo', { variant: 'error' });
    }
  };

  const handleUserCreate = async () => {
    if (!selectedUserId || !organization?.id) return;

    setIsUpdatingUser(true);
    try {
      const selectedUser = availableUsers.find(user => user.id === selectedUserId);

      if (!selectedUser) {
        throw new Error('Usuario seleccionado no encontrado');
      }

      const createData = {
        user: {
          id: selectedUser.id,
          name: selectedUser.name || 'Usuario',
          idType: selectedUser.idTypeId || 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
          idNumber: selectedUser.idNumber || '1000000',
          isActive: selectedUser.isActive ?? true,
          address: selectedUser.address || 'No especificada',
          birthdate: selectedUser.birthdate || '1990-01-01T00:00:00.000Z',
          email: selectedUser.email || `${selectedUser.id}@organizacion.com`,
          gender: selectedUser.gender || 'M',
          lastName: selectedUser.lastName || 'Usuario',
          middleName: selectedUser.middleName || '',
          nickName: selectedUser.nickName || selectedUser.name || 'Usuario'
        },
        organization: {
          id: organization.id,
          idType: (organization as any).idTypeId || (organization as any).idType?.id || 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
          idNumber: organization.idNumber || 'ORG001',
          name: organization.name || 'Organización',
          logo: organization.logo || 'https://s3.amazonaws.com/default-logo.jpg',
          parentOrganizationId: organization.parentOrganizationId || null,
          isActive: organization.isActive ?? true
        },
        description: userDescription || 'Usuario de la organización',
        name: userName || `${selectedUser.name} en ${organization.name}` || 'Usuario en la organización',
        isActive: true,
        isOwner: isOwner
      };

      await api.post(ENDPOINTS.organizationUsers.addUserToOrganizationAdmin(organization.id), createData);

      if (refetchDetails) {
        refetchDetails();
      }
    } catch (error) {
      console.error('Error creating user relationship:', error);
      throw error;
    } finally {
      setIsUpdatingUser(false);
    }
  };

  const handleUserUpdate = async () => {
    if (!selectedUserId || !organization?.id || !orgDetails?.organizationsUsers?.[0]) return;

    setIsUpdatingUser(true);
    try {
      const organizationUser = orgDetails.organizationsUsers[0];
      let selectedUser: any = availableUsers.find(user => user.id === selectedUserId);

      // If user not in list (because listing is disabled), use the existing user from details
      if (!selectedUser && orgDetails.organizationsUsers[0].user?.id === selectedUserId) {
        selectedUser = orgDetails.organizationsUsers[0].user;
        // Normalize fields that might differ between lists and details
        if (!selectedUser.idTypeId && selectedUser.idType?.id) {
          selectedUser.idTypeId = selectedUser.idType.id;
        }
      }

      if (!selectedUser) {
        throw new Error('Usuario seleccionado no encontrado');
      }

      const updateData = {
        id: organizationUser.id,
        user: {
          id: selectedUser.id,
          name: selectedUser.name || 'Usuario',
          idType: selectedUser.idTypeId || 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
          idNumber: selectedUser.idNumber || '1000000',
          isActive: selectedUser.isActive ?? true,
          address: selectedUser.address || 'No especificada',
          birthdate: selectedUser.birthdate || '1990-01-01T00:00:00.000Z',
          email: selectedUser.email || `${selectedUser.id}@organizacion.com`,
          gender: selectedUser.gender || 'M',
          lastName: selectedUser.lastName || 'Usuario',
          middleName: selectedUser.middleName || '',
          nickName: selectedUser.nickName || selectedUser.name || 'Usuario'
        },
        organization: {
          id: organization.id,
          idType: (organization as any).idTypeId || (organization as any).idType?.id || 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
          idNumber: organization.idNumber || 'ORG001',
          name: organization.name || 'Organización',
          logo: organization.logo || 'https://s3.amazonaws.com/default-logo.jpg',
          parentOrganizationId: organization.parentOrganizationId || null,
          isActive: organization.isActive ?? true
        },
        description: userDescription || 'Usuario de la organización',
        name: userName || `${selectedUser.name} en ${organization.name}` || 'Usuario en la organización',
        isActive: organizationUser.isActive ?? true,
        isOwner: isOwner
      };

      await api.put(ENDPOINTS.organizationUsers.updateUserInOrganization(organization.id, organizationUser.id), updateData);

      if (refetchDetails) {
        refetchDetails();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    } finally {
      setIsUpdatingUser(false);
    }
  };

  // Initialize form data when organization details are loaded
  React.useEffect(() => {
    if (orgDetails) {
      // Initialize location data
      if (orgDetails.farmLocations && orgDetails.farmLocations.length > 0) {
        const farmLocation = orgDetails.farmLocations[0];
        setLocationData({
          regionId: '', // Will be set when city details are loaded
          cityId: farmLocation.cityId || '',
          corregimientoId: farmLocation.corregimientoId || '',
          veredaId: farmLocation.veredaId || '',
          latitude: farmLocation.latitude?.toString() || '',
          longitude: farmLocation.longitude?.toString() || '',
          elevation: farmLocation.elevation?.toString() || '',
          area: (farmLocation as any).area?.toString() || ''
        });
      }

      // Initialize socioeconomic data
      if (orgDetails.farmSocioeconomics && orgDetails.farmSocioeconomics.length > 0) {
        const farmSocioeconomic = orgDetails.farmSocioeconomics[0];
        setSocioeconomicData({
          malesFullTimeWorkers: farmSocioeconomic.malesFullTimeWorkers?.toString() || '',
          malesTemporalWorkers: farmSocioeconomic.malesTemporalWorkers?.toString() || '',
          femaleFullTimeWorkers: farmSocioeconomic.femaleFullTimeWorkers?.toString() || '',
          femaleTemporalWorkers: farmSocioeconomic.femaleTemporalWorkers?.toString() || ''
        });
      }

      // Reset user-related state first
      setSelectedUserId('');
      setUserName('');
      setUserDescription('');
      setIsOwner(false);
      setOriginalUserData(null);

      // Initialize selected user from organizationUsers
      if (orgDetails.organizationsUsers && orgDetails.organizationsUsers.length > 0) {
        const organizationUser = orgDetails.organizationsUsers[0];
        setSelectedUserId(organizationUser.user?.id || '');
        setUserName(organizationUser.name || '');
        setUserDescription(organizationUser.description || '');
        setIsOwner(organizationUser.isOwner ?? false);

        // Initialize new fields with existing data
        setFarmName(orgDetails.name || '');
        setFarmIdNumber(orgDetails.idNumber || '');
        setUserFirstName(organizationUser.user?.name || '');
        setUserLastName(organizationUser.user?.lastName || '');

        // Save original data for restoration
        setOriginalUserData({
          name: organizationUser.name || '',
          description: organizationUser.description || '',
          isOwner: organizationUser.isOwner ?? false
        });
      } else {
        // Initialize farm data even if no user
        setFarmName(orgDetails.name || '');
        setFarmIdNumber(orgDetails.idNumber || '');
        setUserFirstName('');
        setUserLastName('');
      }
    }
  }, [orgDetails]);

  // Set region when city details are loaded
  React.useEffect(() => {
    if (cityDetails?.data?.regionId && cityDetails.data.regionId !== selectedRegionId) {
      setLocationData(prev => ({ ...prev, regionId: cityDetails.data.regionId }));
      setSelectedRegionId(cityDetails.data.regionId);
    }
  }, [cityDetails, selectedRegionId]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setSelectedRegionId('');
      setLocationData({
        regionId: '',
        cityId: '',
        corregimientoId: '',
        veredaId: '',
        latitude: '',
        longitude: '',
        elevation: '',
        area: ''
      });
      setSocioeconomicData({
        malesFullTimeWorkers: '',
        malesTemporalWorkers: '',
        femaleFullTimeWorkers: '',
        femaleTemporalWorkers: ''
      });
      // Reset user-related state
      setSelectedUserId('');
      setUserName('');
      setUserDescription('');
      setIsOwner(false);
      setOriginalUserData(null);
      // Reset new fields
      setFarmName('');
      setFarmIdNumber('');
      setUserFirstName('');
      setUserLastName('');
      // Reset logo state
      setSelectedLogoFile(null);
      setLogoPreview(null);
    }
  }, [open]);

  const handleLocationChange = (field: string, value: string) => {
    setLocationData(prev => ({ ...prev, [field]: value }));

    // If region changes, reset city and vereda
    if (field === 'regionId') {
      setSelectedRegionId(value);
      setLocationData(prev => ({
        ...prev,
        cityId: '',
        veredaId: '',
        corregimientoId: ''
      }));
    }

    // If city changes, reset vereda
    if (field === 'cityId') {
      setLocationData(prev => ({
        ...prev,
        veredaId: '',
        corregimientoId: ''
      }));
    }
  };

  const handleSocioeconomicChange = (field: string, value: string) => {
    setSocioeconomicData(prev => ({ ...prev, [field]: value }));
  };

  // Handle opening create modal
  const handleOpenCreateModal = (type: 'region' | 'city' | 'vereda' | 'corregimiento') => {
    setCreateModalType(type);
    setCreateModalOpen(true);
    // Reset form states when opening modal
    setSelectedCountryId('');
    setSelectedRegionForCity('');
    setSelectedCityForVereda('');
    setSelectedCorregimientoForVereda('');
    setSelectedCityForCorregimiento('');
  };

  // Handle saving new entity
  const handleSaveEntity = async (data: any) => {
    setCreateModalLoading(true);
    try {
      let endpoint = '';
      let payload = {};

      switch (createModalType) {
        case 'region':
          endpoint = ENDPOINTS.regions.create();
          payload = {
            name: data.name,
            countryId: data.countryId,
            isActive: data.isActive
          };
          break;
        case 'city':
          endpoint = ENDPOINTS.cities.create();
          payload = {
            name: data.name,
            regionId: data.regionId,
            isActive: data.isActive
          };
          break;
        case 'vereda':
          endpoint = ENDPOINTS.veredas.create();
          payload = {
            name: data.name,
            cityId: data.cityId,
            corregimientoId: data.corregimientoId,
            isActive: data.isActive
          };
          break;
        case 'corregimiento':
          endpoint = ENDPOINTS.corregimientos.create();
          payload = {
            name: data.name,
            cityId: data.cityId,
            isActive: data.isActive
          };
          break;
      }

      await api.post(endpoint, payload);
      enqueueSnackbar(`${createModalType === 'region' ? 'Departamento' : createModalType === 'city' ? 'Municipio' : createModalType === 'vereda' ? 'Vereda' : 'Corregimiento'} creado exitosamente`, { variant: 'success' });

      // Close modal and refresh data
      setCreateModalOpen(false);

      // Refresh the page or refetch data
      window.location.reload();
    } catch (error) {
      console.error('Error creating entity:', error);
      enqueueSnackbar('Error al crear la entidad', { variant: 'error' });
    } finally {
      setCreateModalLoading(false);
    }
  };

  const handleSave = async () => {
    if (!organization?.id) {
      enqueueSnackbar('Error: No se encontró la organización', { variant: 'error' });
      return;
    }

    try {
      if (activeTab === 0) {
        // Organization tab - update only what has changed
        let hasChanges = false;
        const updateResults = [];

        // Check if farm data has changed
        const farmNameChanged = farmName !== (orgDetails?.name || '');
        const farmIdNumberChanged = farmIdNumber !== (orgDetails?.idNumber || '');
        const hasFarmDataChanges = farmNameChanged || farmIdNumberChanged;

        if (hasFarmDataChanges) {
          try {
            await handleFarmDataUpdate();
            updateResults.push('Datos de la finca actualizados correctamente');
            hasChanges = true;
          } catch (error) {
            updateResults.push('Error al actualizar los datos de la finca');
          }
        }

        // Check if logo has changed
        if (selectedLogoFile) {
          try {
            await handleLogoUpdate();
            updateResults.push('Logo actualizado correctamente');
            hasChanges = true;
          } catch (error) {
            updateResults.push('Error al actualizar el logo');
          }
        }

        // Check if user or user properties have changed
        const currentOrgUser = orgDetails?.organizationsUsers?.[0];
        const hasExistingUser = currentOrgUser && currentOrgUser.user;

        // Determine if we need to create or update user relationship
        const userChanged = selectedUserId && currentOrgUser?.user?.id !== selectedUserId;
        const nameChanged = userName !== (currentOrgUser?.name || '');
        const descriptionChanged = userDescription !== (currentOrgUser?.description || '');
        const ownerChanged = isOwner !== (currentOrgUser?.isOwner ?? false);

        // Check if user personal data has changed
        const firstNameChanged = userFirstName !== (currentOrgUser?.user?.name || '');
        const lastNameChanged = userLastName !== (currentOrgUser?.user?.lastName || '');
        const hasUserPersonalDataChanges = firstNameChanged || lastNameChanged;

        const hasUserChanges = userChanged || nameChanged || descriptionChanged || ownerChanged;

        if (hasUserChanges) {
          try {
            if (hasExistingUser) {
              // Update existing user relationship
              await handleUserUpdate();
              updateResults.push('Usuario actualizado correctamente');
            } else {
              // Create new user relationship
              await handleUserCreate();
              updateResults.push('Usuario creado correctamente');
            }
            hasChanges = true;
          } catch (error) {
            updateResults.push(hasExistingUser ? 'Error al actualizar el usuario' : 'Error al crear el usuario');
          }
        }

        // Check if user personal data has changed and update separately
        if (hasUserPersonalDataChanges) {
          try {
            await handleUserDataUpdate();
            updateResults.push('Datos del usuario actualizados correctamente');
            hasChanges = true;
          } catch (error) {
            updateResults.push('Error al actualizar los datos del usuario');
          }
        }

        if (!hasChanges) {
          enqueueSnackbar('No hay cambios para actualizar', { variant: 'info' });
        } else {
          // Show combined results
          const successCount = updateResults.filter(result => result.includes('correctamente')).length;
          const errorCount = updateResults.filter(result => result.includes('Error')).length;

          if (errorCount === 0) {
            enqueueSnackbar(`Actualización exitosa: ${updateResults.join(', ')}`, { variant: 'success' });
          } else if (successCount > 0) {
            enqueueSnackbar(`Actualización parcial: ${updateResults.join(', ')}`, { variant: 'warning' });
          } else {
            enqueueSnackbar(`Error en la actualización: ${updateResults.join(', ')}`, { variant: 'error' });
          }
        }
        return;
      } else if (activeTab === 1) {
        // Location tab
        await handleLocationSave();
      } else if (activeTab === 2) {
        // Socioeconomic tab
        await handleSocioeconomicSave();
      } else if (activeTab === 3) {
        // Groups tab - no save action needed for now
        enqueueSnackbar('Los grupos se gestionan directamente desde esta pestaña', { variant: 'info' });
        return;
      }

      enqueueSnackbar('Datos guardados exitosamente', { variant: 'success' });
    } catch (error) {
      console.error('Error saving data:', error);
      enqueueSnackbar('Error al guardar los datos', { variant: 'error' });
    }
  };

  const handleLocationSave = async () => {
    const hasExistingLocation = orgDetails?.farmLocations && orgDetails.farmLocations.length > 0;
    const locationId = hasExistingLocation ? orgDetails.farmLocations[0].id : null;

    const locationPayload = {
      area: locationData.area ? parseFloat(locationData.area) : 0,
      cityId: locationData.cityId,
      corregimientoId: locationData.corregimientoId || undefined,
      elevation: locationData.elevation ? parseFloat(locationData.elevation) : 0,
      geometry: '', // You might want to calculate this based on coordinates
      latitude: locationData.latitude ? parseFloat(locationData.latitude) : 0,
      longitude: locationData.longitude ? parseFloat(locationData.longitude) : 0,
      organizationId: org.id,
      veredaId: locationData.veredaId,
      isActive: true
    };

    if (hasExistingLocation && locationId) {
      // Update existing location
      await updateFarmLocationMutation.mutateAsync({ id: locationId, data: locationPayload });
    } else {
      // Create new location
      await createFarmLocationMutation.mutateAsync(locationPayload);
    }
  };

  const handleSocioeconomicSave = async () => {
    const hasExistingSocioeconomic = orgDetails?.farmSocioeconomics && orgDetails.farmSocioeconomics.length > 0;
    const socioeconomicId = hasExistingSocioeconomic ? orgDetails.farmSocioeconomics[0].id : null;

    const socioeconomicPayload = {
      malesFullTimeWorkers: socioeconomicData.malesFullTimeWorkers ? parseInt(socioeconomicData.malesFullTimeWorkers) : null,
      malesTemporalWorkers: socioeconomicData.malesTemporalWorkers ? parseInt(socioeconomicData.malesTemporalWorkers) : null,
      femaleFullTimeWorkers: socioeconomicData.femaleFullTimeWorkers ? parseInt(socioeconomicData.femaleFullTimeWorkers) : null,
      femaleTemporalWorkers: socioeconomicData.femaleTemporalWorkers ? parseInt(socioeconomicData.femaleTemporalWorkers) : null,
      organizationId: org.id,
      isActive: true
    };

    if (hasExistingSocioeconomic && socioeconomicId) {
      // Update existing socioeconomic data
      await api.put(ENDPOINTS.farmSocioeconomics.update(org.id, socioeconomicId), socioeconomicPayload);
    } else {
      // Create new socioeconomic data
      await api.post(ENDPOINTS.farmSocioeconomics.create(org.id), socioeconomicPayload);
    }
  };

  // Don't render if organization is null
  if (!organization) {
    return null;
  }

  // Type assertion after null check
  const org = organization as OrganizationDto;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className={styles.dialog}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div">
            Editar Organización
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            className={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <Box className={styles.content}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className={styles.tabs}
            variant="fullWidth"
          >
            <Tab
              icon={<BusinessIcon />}
              label="Organización"
              iconPosition="start"
              className={styles.tab}
            />
            <Tab
              icon={<LocationOnIcon />}
              label="Ubicación"
              iconPosition="start"
              className={styles.tab}
            />
            <Tab
              icon={<PeopleIcon />}
              label="Socioeconómicos"
              iconPosition="start"
              className={styles.tab}
            />
            <Tab
              icon={<GroupIcon />}
              label="Mis Grupos"
              iconPosition="start"
              className={styles.tab}
            />
          </Tabs>

          <Box className={styles.tabContent}>
            {isLoadingDetails ? (
              <Box className={styles.loadingContainer}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Cargando información de la organización...
                </Typography>
              </Box>
            ) : (
              <>
                {activeTab === 0 && (
                  <Box className={styles.tabPanel}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      Datos de Organización
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Left Column - Logo and Farm/User Info */}
                      <Grid item xs={12} md={6}>
                        <Grid container spacing={1}>
                          {/* Logo Section - Top Row */}
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                              Logo de la Organización
                            </Typography>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2,
                                minHeight: 250
                              }}
                            >
                              {/* Current Logo Display */}
                              {orgDetails?.logo ? (
                                <Box sx={{ textAlign: 'center' }}>
                                  <img
                                    src={orgDetails.logo}
                                    alt={`${orgDetails.name} logo`}
                                    style={{
                                      maxWidth: '180px',
                                      maxHeight: '120px',
                                      objectFit: 'contain',
                                      border: '1px solid #e0e0e0',
                                      borderRadius: '8px'
                                    }}
                                  />
                                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Logo actual
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ textAlign: 'center' }}>
                                  <BusinessIcon sx={{ fontSize: 50, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    No hay logo actual
                                  </Typography>
                                </Box>
                              )}

                              {/* Logo Preview */}
                              {logoPreview && (
                                <Box sx={{ textAlign: 'center' }}>
                                  <img
                                    src={logoPreview}
                                    alt="Vista previa del nuevo logo"
                                    style={{
                                      maxWidth: '180px',
                                      maxHeight: '120px',
                                      objectFit: 'contain',
                                      border: '2px solid #1976d2',
                                      borderRadius: '8px'
                                    }}
                                  />
                                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                                    Vista previa del nuevo logo
                                  </Typography>
                                </Box>
                              )}

                              {/* Upload Button */}
                              <Button
                                variant="outlined"
                                startIcon={<CloudUploadIcon />}
                                onClick={handleLogoUpload}
                                disabled={isUpdatingLogo}
                                sx={{ mt: 1 }}
                                size="small"
                              >
                                {selectedLogoFile ? 'Cambiar Imagen' : 'Subir Logo'}
                              </Button>

                              {/* Hidden File Input */}
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={handleLogoFileSelect}
                                style={{ display: 'none' }}
                              />

                              <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 250, fontSize: '0.75rem' }}>
                                Formatos: PNG, JPG, JPEG. Máx: 5MB
                              </Typography>

                              {selectedLogoFile && (
                                <Typography variant="body2" color="primary" sx={{ textAlign: 'center', mt: 1, fontWeight: 'medium', fontSize: '0.8rem' }}>
                                  Usa el botón &quot;Actualizar Organización&quot; para guardar los cambios
                                </Typography>
                              )}
                            </Paper>
                          </Grid>

                          {/* Farm and User Info Section - Bottom Row */}
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                              Información de la Finca y Usuario
                            </Typography>
                            <Paper
                              elevation={2}
                              sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                minHeight: 200
                              }}
                            >
                              <TextField
                                fullWidth
                                label="Nombre de la finca"
                                value={farmName}
                                onChange={(e) => setFarmName(e.target.value)}
                                placeholder="Ingrese el nombre de la finca"
                                variant="outlined"
                              />

                              <TextField
                                fullWidth
                                label="Cédula de la finca"
                                value={farmIdNumber}
                                onChange={(e) => setFarmIdNumber(e.target.value)}
                                placeholder="Ingrese la cédula de la finca"
                                variant="outlined"
                              />

                              <TextField
                                fullWidth
                                label="Nombre del usuario"
                                value={userFirstName}
                                onChange={(e) => setUserFirstName(e.target.value)}
                                placeholder="Ingrese el nombre"
                                variant="outlined"
                              />

                              <TextField
                                fullWidth
                                label="Apellido del usuario"
                                value={userLastName}
                                onChange={(e) => setUserLastName(e.target.value)}
                                placeholder="Ingrese el apellido"
                                variant="outlined"
                              />
                            </Paper>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Right Column - User Selection Section */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                          Usuario de la Organización
                        </Typography>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            minHeight: 'fit-content'
                          }}
                        >
                          {/* User selection disabled
                          <FormControl fullWidth>
                            <InputLabel>Seleccionar Usuario</InputLabel>
                            <Select
                              value={selectedUserId}
                              onChange={(e) => handleUserSelectionChange(e.target.value)}
                              label="Seleccionar Usuario"
                            >
                              {availableUsers.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                  {user.name} {user.lastName && `(${user.lastName})`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          */}

                          <TextField
                            fullWidth
                            label="Nombre en la organización"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            placeholder="Ej: Santiago en Segunda hija"
                          />

                          <TextField
                            fullWidth
                            label="Descripción"
                            value={userDescription}
                            onChange={(e) => setUserDescription(e.target.value)}
                            placeholder="Descripción del usuario en la organización"
                            multiline
                            rows={2}
                          />

                          <FormControlLabel
                            control={
                              <Switch
                                checked={isOwner}
                                onChange={(e) => setIsOwner(e.target.checked)}
                              />
                            }
                            label="Es Propietario"
                          />

                          {orgDetails?.organizationsUsers?.[0]?.user ? (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                Usuario actual:
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {orgDetails.organizationsUsers[0].user?.name || 'N/A'}
                                {orgDetails.organizationsUsers[0].isOwner && (
                                  <Chip
                                    label="Propietario"
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                              {orgDetails.organizationsUsers[0].name && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                  <strong>Nombre en org:</strong> {orgDetails.organizationsUsers[0].name}
                                </Typography>
                              )}
                              {orgDetails.organizationsUsers[0].description && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                  <strong>Descripción:</strong> {orgDetails.organizationsUsers[0].description}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="caption" color="textSecondary">
                                Sin usuario asignado
                              </Typography>
                            </Box>
                          )}

                          {(() => {
                            const currentOrgUser = orgDetails?.organizationsUsers?.[0];
                            const hasExistingUser = currentOrgUser && currentOrgUser.user;

                            const userChanged = selectedUserId && currentOrgUser?.user?.id !== selectedUserId;
                            const nameChanged = userName !== (currentOrgUser?.name || '');
                            const descriptionChanged = userDescription !== (currentOrgUser?.description || '');
                            const ownerChanged = isOwner !== (currentOrgUser?.isOwner ?? false);
                            const hasChanges = userChanged || nameChanged || descriptionChanged || ownerChanged;

                            // For organizations without user, any field change is a change
                            const hasChangesForNewUser = !hasExistingUser && (selectedUserId || userName || userDescription || isOwner);

                            return (hasChanges || hasChangesForNewUser) ? (
                              <Typography variant="body2" color="primary" sx={{ textAlign: 'center', mt: 1, fontWeight: 'medium' }}>
                                {hasExistingUser
                                  ? 'Hay cambios pendientes. Usa el botón "Actualizar Organización" para guardar'
                                  : 'Hay datos para crear usuario. Usa el botón "Actualizar Organización" para guardar'
                                }
                              </Typography>
                            ) : null;
                          })()}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box className={styles.tabPanel}>
                    <Typography variant="h6" gutterBottom>
                      Información de Ubicación
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FormControl fullWidth>
                            <InputLabel>Región</InputLabel>
                            <Select
                              value={locationData.regionId}
                              onChange={(e) => handleLocationChange('regionId', e.target.value)}
                              label="Región"
                            >
                              {regions?.data?.map((region) => (
                                <MenuItem key={region.id} value={region.id}>
                                  {region.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenCreateModal('region')}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              height: '56px',
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                borderColor: '#1565c0',
                              }
                            }}
                            title="Crear nuevo departamento"
                          >
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FormControl fullWidth>
                            <InputLabel>Ciudad</InputLabel>
                            <Select
                              value={locationData.cityId}
                              onChange={(e) => handleLocationChange('cityId', e.target.value)}
                              label="Ciudad"
                              disabled={!locationData.regionId}
                            >
                              {cities?.data?.map((city) => (
                                <MenuItem key={city.id} value={city.id}>
                                  {city.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenCreateModal('city')}
                            disabled={!locationData.regionId}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              height: '56px',
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                borderColor: '#1565c0',
                              }
                            }}
                            title="Crear nuevo municipio"
                          >
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FormControl fullWidth>
                            <InputLabel>Corregimiento</InputLabel>
                            <Select
                              value={locationData.corregimientoId}
                              onChange={(e) => handleLocationChange('corregimientoId', e.target.value)}
                              label="Corregimiento"
                            >
                              {corregimientos?.data?.map((corregimiento) => (
                                <MenuItem key={corregimiento.id} value={corregimiento.id}>
                                  {corregimiento.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenCreateModal('corregimiento')}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              height: '56px',
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                borderColor: '#1565c0',
                              }
                            }}
                            title="Crear nuevo corregimiento"
                          >
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FormControl fullWidth>
                            <InputLabel>Vereda</InputLabel>
                            <Select
                              value={locationData.veredaId}
                              onChange={(e) => handleLocationChange('veredaId', e.target.value)}
                              label="Vereda"
                              disabled={!locationData.cityId}
                            >
                              {veredas?.data?.map((vereda) => (
                                <MenuItem key={vereda.id} value={vereda.id}>
                                  {vereda.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenCreateModal('vereda')}
                            disabled={!locationData.cityId}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              height: '56px',
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                borderColor: '#1565c0',
                              }
                            }}
                            title="Crear nueva vereda"
                          >
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Área (hectáreas)"
                          type="number"
                          value={locationData.area}
                          onChange={(e) => handleLocationChange('area', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Latitud"
                          type="number"
                          value={locationData.latitude}
                          onChange={(e) => handleLocationChange('latitude', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Longitud"
                          type="number"
                          value={locationData.longitude}
                          onChange={(e) => handleLocationChange('longitude', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Elevación (m)"
                          type="number"
                          value={locationData.elevation}
                          onChange={(e) => handleLocationChange('elevation', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box className={styles.tabPanel}>
                    <Typography variant="h6" gutterBottom>
                      Información Socioeconómica
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Trabajadores masculinos tiempo completo"
                          type="number"
                          value={socioeconomicData.malesFullTimeWorkers}
                          onChange={(e) => handleSocioeconomicChange('malesFullTimeWorkers', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Trabajadores masculinos temporales"
                          type="number"
                          value={socioeconomicData.malesTemporalWorkers}
                          onChange={(e) => handleSocioeconomicChange('malesTemporalWorkers', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Trabajadoras femeninas tiempo completo"
                          type="number"
                          value={socioeconomicData.femaleFullTimeWorkers}
                          onChange={(e) => handleSocioeconomicChange('femaleFullTimeWorkers', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Trabajadoras femeninas temporales"
                          type="number"
                          value={socioeconomicData.femaleTemporalWorkers}
                          onChange={(e) => handleSocioeconomicChange('femaleTemporalWorkers', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {activeTab === 3 && (
                  <Box className={styles.tabPanel}>
                    <Typography variant="h6" gutterBottom>
                      Mis Grupos
                    </Typography>
                    <Grid container spacing={3}>
                      {/* Columna Izquierda - Mis Grupos Actuales */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                          Mis Grupos Actuales
                        </Typography>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            minHeight: 300
                          }}
                        >
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Grupos a los que perteneces actualmente
                          </Typography>

                          {isLoadingCurrentGroups ? (
                            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                              <CircularProgress />
                            </Box>
                          ) : currentGroups && currentGroups.length > 0 ? (
                            <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                              {currentGroups.map((groupOrg: any) => (
                                <Paper
                                  key={groupOrg.id}
                                  elevation={1}
                                  sx={{
                                    p: 2,
                                    mb: 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    bgcolor: 'primary.50'
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                      {groupOrg.group?.name || 'Grupo'}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {groupOrg.group?.description || 'Sin descripción'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      label="Miembro"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={deleteGroupMutation.isPending}
                                      onClick={() => handleLeaveGroup(groupOrg)}
                                      startIcon={deleteGroupMutation.isPending ? <CircularProgress size={16} /> : <ExitToAppIcon />}
                                    >
                                      {deleteGroupMutation.isPending ? 'Saliendo...' : 'Salir'}
                                    </Button>
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          ) : (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" color="textSecondary">
                                No estás afiliado a ningún grupo
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>

                      {/* Columna Derecha - Grupos Disponibles */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                          Grupos Disponibles
                        </Typography>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            minHeight: 300
                          }}
                        >
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Grupos disponibles para afiliarte
                          </Typography>

                          {isLoadingGroups ? (
                            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                              <CircularProgress />
                            </Box>
                          ) : filteredAvailableGroups && filteredAvailableGroups.length > 0 ? (
                            <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                              {filteredAvailableGroups.map((group: any) => (
                                <Paper
                                  key={group.id}
                                  elevation={1}
                                  sx={{
                                    p: 2,
                                    mb: 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: 'grey.50'
                                    }
                                  }}
                                  onClick={() => setSelectedGroup(group.id)}
                                >
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                      {group.name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {group.description}
                                    </Typography>
                                  </Box>
                                  <Button
                                    size="small"
                                    variant={selectedGroup === group.id ? "contained" : "outlined"}
                                    disabled={isJoiningGroup}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleJoinGroup(group.id);
                                    }}
                                    startIcon={isJoiningGroup && selectedGroup === group.id ? <CircularProgress size={16} /> : <AddIcon />}
                                  >
                                    {isJoiningGroup && selectedGroup === group.id ? 'Uniéndose...' : 'Unirse'}
                                  </Button>
                                </Paper>
                              ))}
                            </Box>
                          ) : (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="body2" color="textSecondary" textAlign="center">
                                No hay grupos disponibles
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Save Button */}
          <Box className={styles.saveButtonContainer}>
            <Button
              variant="contained"
              startIcon={(createFarmLocationMutation.isPending || updateFarmLocationMutation.isPending || isUpdatingLogo || isUpdatingUser) ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={(createFarmLocationMutation.isPending || updateFarmLocationMutation.isPending || isUpdatingLogo || isUpdatingUser) || isLoadingDetails}
              className={styles.saveButton}
            >
              {(createFarmLocationMutation.isPending || updateFarmLocationMutation.isPending || isUpdatingLogo || isUpdatingUser)
                ? 'Guardando...'
                : activeTab === 0
                  ? 'Actualizar Organización'
                  : activeTab === 1
                    ? 'Guardar Ubicación'
                    : activeTab === 2
                      ? 'Guardar Socioeconómicos'
                      : 'Gestionar Grupos'
              }
            </Button>
          </Box>
        </Box>
      </DialogContent>

      {/* Create Entity Modal */}
      <CreateEntityModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title={createModalType === 'region' ? 'Departamento' : createModalType === 'city' ? 'Municipio' : createModalType === 'vereda' ? 'Vereda' : 'Corregimiento'}
        entityType={createModalType}
        onSave={handleSaveEntity}
        loading={createModalLoading}
        externalFields={
          createModalType === 'region'
            ? { countryId: selectedCountryId }
            : createModalType === 'city'
              ? { regionId: selectedRegionForCity }
              : createModalType === 'vereda'
                ? { cityId: selectedCityForVereda, corregimientoId: selectedCorregimientoForVereda }
                : createModalType === 'corregimiento'
                  ? { cityId: selectedCityForCorregimiento }
                  : {}
        }
        additionalFields={
          createModalType === 'region' ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>País</InputLabel>
              <Select
                value={selectedCountryId}
                onChange={(e) => setSelectedCountryId(e.target.value)}
                label="País"
              >
                {countries?.data?.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : createModalType === 'city' ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>Departamento</InputLabel>
              <Select
                value={selectedRegionForCity}
                onChange={(e) => setSelectedRegionForCity(e.target.value)}
                label="Departamento"
              >
                {regions?.data?.map((region) => (
                  <MenuItem key={region.id} value={region.id}>
                    {region.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : createModalType === 'vereda' ? (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>Ciudad</InputLabel>
                <Select
                  value={selectedCityForVereda}
                  onChange={(e) => setSelectedCityForVereda(e.target.value)}
                  label="Ciudad"
                >
                  {allCities?.map((city: any) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Corregimiento</InputLabel>
                <Select
                  value={selectedCorregimientoForVereda}
                  onChange={(e) => setSelectedCorregimientoForVereda(e.target.value)}
                  label="Corregimiento"
                >
                  {corregimientos?.data?.map((corregimiento) => (
                    <MenuItem key={corregimiento.id} value={corregimiento.id}>
                      {corregimiento.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : createModalType === 'corregimiento' ? (
            <FormControl fullWidth margin="normal">
              <InputLabel>Ciudad</InputLabel>
              <Select
                value={selectedCityForCorregimiento}
                onChange={(e) => setSelectedCityForCorregimiento(e.target.value)}
                label="Ciudad"
              >
                {allCities?.map((city: any) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null
        }
      />

    </Dialog>
  );
};

export default EditFarmDetailsModal;
